import { useMemo, useState } from 'react';
import { saveCotacao } from '@/services/cotacoesStore';
import { useAuth } from '@/hooks/useAuth';
import { useClientes, useMateriais } from '@/hooks/useDB';
import { canAccessClient, canAccessMaterial } from '@/auth/access';

interface Props { onSaved: () => void }

const today = () => new Date().toISOString().slice(0, 10);

export default function LancamentoForm({ onSaved }: Props) {
  const { user } = useAuth();
  const { clientes } = useClientes();
  const { materiais } = useMateriais();
  const accessibleClients = useMemo(() => clientes.filter((item) => canAccessClient(user, item)), [clientes, user]);
  const accessibleMaterials = useMemo(() => materiais.filter((item) => canAccessMaterial(user, item)), [materiais, user]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clienteCodigo, setClienteCodigo] = useState('');
  const [materialCodigo, setMaterialCodigo] = useState('');
  const [data, setData] = useState(today());
  const [moeda, setMoeda] = useState<'BRL' | 'USD' | 'EUR'>('BRL');
  const [valor, setValor] = useState('');
  const [cotacao, setCotacao] = useState('1');
  const [margem, setMargem] = useState('40');
  const [condicao, setCondicao] = useState('');

  const cliente = useMemo(() => accessibleClients.find((item) => item.codigo === clienteCodigo), [clienteCodigo, accessibleClients]);
  const material = useMemo(() => accessibleMaterials.find((item) => item.codigo === materialCodigo), [materialCodigo, accessibleMaterials]);

  const selectCliente = (codigo: string) => {
    setClienteCodigo(codigo);
    const selected = accessibleClients.find((item) => item.codigo === codigo);
    if (selected?.condicaoPagamento) setCondicao(selected.condicaoPagamento);
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!cliente || !material) return;
    setSaving(true);
    const total = Number(valor.replace(',', '.'));
    const exchange = moeda === 'BRL' ? 1 : Number(cotacao.replace(',', '.'));
    const margin = Number(margem.replace(',', '.'));
    const precoNet = total;
    await saveCotacao({
      clienteCodigo: cliente.codigo,
      clienteNome: cliente.nome,
      vendedor: user?.name || 'Usuário',
      divisao: cliente.divisao,
      moeda,
      itens: [{
        materialCodigo: material.codigo,
        materialDescricao: material.descricao,
        unidadeMedida: material.unidadeMedida,
        custoReposicao: material.custoReposicao,
        icms: material.icmsRs,
        ipi: material.ipi / 100,
        fator: 1,
        precoNetMinimo: precoNet,
        precoComImpostosMinimo: precoNet,
        precoNegociado: precoNet,
        precoNegociadoComImpostos: precoNet,
        margemRealizada: margin,
        precoUnitarioConvertido: precoNet,
      }],
      total,
      totalBrl: total * exchange,
      margemMedia: margin,
      beneficioFiscal: false,
      isentoIpi: false,
      isentoIcms: false,
      isentoPisCofins: false,
      condicaoPagamento: condicao,
      cotacaoUsd: moeda === 'USD' ? exchange : 1,
      cotacaoEur: moeda === 'EUR' ? exchange : 1,
      margemAlvo: 40,
      createdAt: new Date(`${data}T12:00:00`).toISOString(),
    });
    setSaving(false);
    setOpen(false);
    setValor('');
    setMaterialCodigo('');
    onSaved();
  };

  if (!open) {
    return <button onClick={() => setOpen(true)} className="px-4 py-2.5 rounded-lg bg-primary-500 text-white font-semibold text-sm shadow-sm hover:bg-primary-600"><i className="ri-add-line mr-1" />Adicionar lançamento</button>;
  }

  return (
    <form onSubmit={save} className="bg-white border border-primary-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4"><div><h2 className="font-heading text-lg font-bold text-foreground-950">Novo lançamento</h2><p className="text-sm text-foreground-700">Preencha as informações que alimentarão os indicadores.</p></div><button type="button" onClick={() => setOpen(false)} className="w-9 h-9 rounded-lg bg-background-100 text-foreground-900"><i className="ri-close-line" /></button></div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        <label className="text-sm font-semibold text-foreground-900">Cliente
          <select required value={clienteCodigo} onChange={(e) => selectCliente(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal">
            <option value="">Selecione...</option>{accessibleClients.map((item) => <option key={item.codigo} value={item.codigo}>{item.codigo} — {item.nome}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-foreground-900">Produto
          <select required value={materialCodigo} onChange={(e) => setMaterialCodigo(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal">
            <option value="">Selecione...</option>{accessibleMaterials.map((item) => <option key={item.codigo} value={item.codigo}>{item.codigo} — {item.descricao}</option>)}
          </select>
        </label>
        <label className="text-sm font-semibold text-foreground-900">Data
          <input required type="date" value={data} onChange={(e) => setData(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal" />
        </label>
        <label className="text-sm font-semibold text-foreground-900">Moeda
          <select value={moeda} onChange={(e) => setMoeda(e.target.value as typeof moeda)} className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal"><option>BRL</option><option>USD</option><option>EUR</option></select>
        </label>
        <label className="text-sm font-semibold text-foreground-900">Valor total
          <input required type="number" min="0.01" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="0,00" className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal" />
        </label>
        {moeda !== 'BRL' && <label className="text-sm font-semibold text-foreground-900">Cotação para BRL
          <input required type="number" min="0.0001" step="0.0001" value={cotacao} onChange={(e) => setCotacao(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal" />
        </label>}
        <label className="text-sm font-semibold text-foreground-900">Margem (%)
          <input required type="number" min="-100" max="100" step="0.1" value={margem} onChange={(e) => setMargem(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal" />
        </label>
        <label className="text-sm font-semibold text-foreground-900 md:col-span-2">Condição de pagamento
          <input value={condicao} onChange={(e) => setCondicao(e.target.value)} placeholder="Ex.: 30/60/90" className="mt-1.5 w-full px-3 py-2.5 bg-white border border-background-300 rounded-lg text-foreground-950 font-normal" />
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border text-foreground-900 font-semibold">Cancelar</button><button disabled={saving} className="px-5 py-2 rounded-lg bg-primary-500 text-white font-semibold disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar lançamento'}</button></div>
    </form>
  );
}
